import { getThemePreference, setThemePreference } from '../../router/guards';
import { navigate } from '../../router/router';
import { apiFetch } from '../../services/api-client';
import { fetchIndices, fetchBreadth } from '../../services/market-data';
import { fetchPriorityHeadlines } from '../../services/news-feed';
import { getItem, setItem } from '../../services/storage';
import { NIFTY50_COMPANIES } from '../../config/india-companies';
import { formatIST, getISTNow, getMarketStatus } from '../../utils/ist-time';
import { getChartRenderTelemetrySnapshot } from '../../charts/engine';
import { renderPhase2GlobalTelemetryEngine } from '../charts/Phase2GlobalTelemetryEngine';
import { renderTerminalTopBar } from './TerminalTopBar';
import { renderTerminalNav } from './TerminalNav';
import { renderTerminalTicker } from './TerminalTicker';
import { renderPanelHeader, renderPanelTabs, renderStatStrip } from './TerminalPrimitives';
import { renderFooterTelemetryStrip } from './TerminalFooter';

interface CacheTelemetryTotals {
    hits: number;
    misses: number;
    errors: number;
    hitRatePct: number;
}

interface CacheTelemetryResponse {
    totals?: CacheTelemetryTotals;
    timestamp?: string;
}

type ApiHealthResponse = {
  status?: 'ok' | 'degraded';
  latencyMs?: number;
  checks?: Record<string, 'ok' | 'error' | 'timeout'>;
  details?: Record<string, string>;
  timestamp?: string;
};

type StreamHealthState = 'CONNECTING' | 'CONNECTED' | 'DEGRADED' | 'RECONNECTING' | 'STALE';

type OptionsClusterAlertEventDetail = {
  symbol?: string;
  expiry?: string;
  level?: 'normal' | 'elevated' | 'critical';
  cluster?: string;
  dwellMs?: number;
  score?: number;
  summary?: string;
};

type CommandAction = {
    route: string;
    event: string;
    payload?: Record<string, string>;
  statusLabel?: string;
};

type WatchlistEntryType = 'index' | 'stock' | 'breadth';

type WatchlistEntry = {
  id: string;
  type: WatchlistEntryType;
  label: string;
  symbol: string;
  meta?: string;
};

type WatchlistCatalogEntry = WatchlistEntry & {
  keywords: string[];
};

const WATCHLIST_STORAGE_KEY = 'terminal_watchlist';
const WATCHLIST_MAX_ITEMS = 14;
const DEFAULT_WATCHLIST: WatchlistEntry[] = [
  { id: 'index:nifty-50', type: 'index', label: 'Nifty 50', symbol: 'NIFTY 50', meta: 'Index' },
  { id: 'index:nifty-bank', type: 'index', label: 'Nifty Bank', symbol: 'NIFTY BANK', meta: 'Index' },
  { id: 'index:india-vix', type: 'index', label: 'India VIX', symbol: 'INDIA VIX', meta: 'Volatility' },
  { id: 'index:nifty-it', type: 'index', label: 'Nifty IT', symbol: 'NIFTY IT', meta: 'Sector index' },
  { id: 'index:nifty-auto', type: 'index', label: 'Nifty Auto', symbol: 'NIFTY AUTO', meta: 'Sector index' },
  { id: 'index:nifty-fmcg', type: 'index', label: 'Nifty FMCG', symbol: 'NIFTY FMCG', meta: 'Sector index' },
  { id: 'index:nifty-metal', type: 'index', label: 'Nifty Metal', symbol: 'NIFTY METAL', meta: 'Sector index' },
  { id: 'index:nifty-fin-service', type: 'index', label: 'Nifty Fin Service', symbol: 'NIFTY FIN SERVICE', meta: 'Sector index' },
  { id: 'breadth:ad-ratio', type: 'breadth', label: 'Breadth', symbol: 'A/D RATIO', meta: 'Market internals' },
  { id: 'stock:RELIANCE', type: 'stock', label: 'Reliance Industries', symbol: 'RELIANCE', meta: 'energy' },
  { id: 'stock:HDFCBANK', type: 'stock', label: 'HDFC Bank', symbol: 'HDFCBANK', meta: 'banking' },
  { id: 'stock:INFY', type: 'stock', label: 'Infosys', symbol: 'INFY', meta: 'it' },
];

const COMMON_INDEX_CATALOG: WatchlistCatalogEntry[] = [
  { id: 'index:nifty-50', type: 'index', label: 'Nifty 50', symbol: 'NIFTY 50', meta: 'Index', keywords: ['nifty 50', 'nifty50', 'benchmark'] },
  { id: 'index:nifty-bank', type: 'index', label: 'Nifty Bank', symbol: 'NIFTY BANK', meta: 'Index', keywords: ['bank nifty', 'nifty bank', 'banks'] },
  { id: 'index:india-vix', type: 'index', label: 'India VIX', symbol: 'INDIA VIX', meta: 'Volatility', keywords: ['india vix', 'vix', 'volatility'] },
  { id: 'index:nifty-it', type: 'index', label: 'Nifty IT', symbol: 'NIFTY IT', meta: 'Sector index', keywords: ['nifty it', 'it'] },
  { id: 'index:nifty-auto', type: 'index', label: 'Nifty Auto', symbol: 'NIFTY AUTO', meta: 'Sector index', keywords: ['nifty auto', 'auto'] },
  { id: 'index:nifty-fmcg', type: 'index', label: 'Nifty FMCG', symbol: 'NIFTY FMCG', meta: 'Sector index', keywords: ['nifty fmcg', 'fmcg'] },
  { id: 'index:nifty-metal', type: 'index', label: 'Nifty Metal', symbol: 'NIFTY METAL', meta: 'Sector index', keywords: ['nifty metal', 'metal'] },
  { id: 'index:nifty-pharma', type: 'index', label: 'Nifty Pharma', symbol: 'NIFTY PHARMA', meta: 'Sector index', keywords: ['nifty pharma', 'pharma'] },
  { id: 'index:nifty-psu-bank', type: 'index', label: 'Nifty PSU Bank', symbol: 'NIFTY PSU BANK', meta: 'Sector index', keywords: ['psu bank', 'nifty psu bank'] },
  { id: 'index:nifty-pvt-bank', type: 'index', label: 'Nifty Pvt Bank', symbol: 'NIFTY PVT BANK', meta: 'Sector index', keywords: ['private bank', 'nifty pvt bank'] },
  { id: 'index:nifty-fin-service', type: 'index', label: 'Nifty Fin Service', symbol: 'NIFTY FIN SERVICE', meta: 'Sector index', keywords: ['nifty fin service', 'financial services'] },
  { id: 'index:nifty-midcap-select', type: 'index', label: 'Nifty Midcap Select', symbol: 'NIFTY MIDCAP SELECT', meta: 'Index', keywords: ['midcap', 'nifty midcap select'] },
  { id: 'index:nifty-realty', type: 'index', label: 'Nifty Realty', symbol: 'NIFTY REALTY', meta: 'Sector index', keywords: ['realty', 'nifty realty'] },
  { id: 'breadth:ad-ratio', type: 'breadth', label: 'Breadth', symbol: 'A/D RATIO', meta: 'Market internals', keywords: ['breadth', 'advance decline', 'a/d ratio'] },
];

const WATCHLIST_CATALOG: WatchlistCatalogEntry[] = [
  ...COMMON_INDEX_CATALOG,
  ...NIFTY50_COMPANIES.map((company) => ({
    id: `stock:${company.symbol}`,
    type: 'stock' as const,
    label: company.name,
    symbol: company.symbol,
    meta: company.sector,
    keywords: [company.name.toLowerCase(), company.symbol.toLowerCase(), company.sector.toLowerCase()],
  })),
];

let shortcutsBound = false;
let watchlistDismissBound = false;
let activeWatchlistTab: 'INDICES' | 'BREADTH' = 'INDICES';
let commandStatusTimer: ReturnType<typeof setTimeout> | null = null;
let optionsAlertBound = false;
let optionsAlertStaleTimer: ReturnType<typeof setTimeout> | null = null;
let optionsAlertStaleAgeInterval: ReturnType<typeof setInterval> | null = null;
let optionsAlertLastEventAt: number | null = null;
const OPTIONS_ALERT_STALE_MS = 12000;
const STREAM_STALE_MS = 30000;
const STREAM_ERROR_SPIKE_WINDOW_MS = 120000;
const STREAM_ERROR_SPIKE_THRESHOLD = 3;
const LATENCY_SERIES_LIMIT = 8;

let telemetryLastSuccessAt: number | null = null;
let telemetryConsecutiveFailures = 0;
let telemetryErrorEvents: number[] = [];
let telemetryLatencySeries: number[] = [];
let previousCacheErrorTotal: number | null = null;

function normalizeKey(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function getWatchlistEntries(): WatchlistEntry[] {
  const stored = getItem<WatchlistEntry[]>(WATCHLIST_STORAGE_KEY);
  if (!Array.isArray(stored) || stored.length === 0) {
    return DEFAULT_WATCHLIST.slice(0, WATCHLIST_MAX_ITEMS);
  }

  const valid = stored
    .filter((item): item is WatchlistEntry => !!item && typeof item.id === 'string' && typeof item.label === 'string' && typeof item.symbol === 'string')
    .slice(0, WATCHLIST_MAX_ITEMS);

  return valid.length > 0 ? valid : DEFAULT_WATCHLIST.slice(0, WATCHLIST_MAX_ITEMS);
}

function saveWatchlistEntries(entries: WatchlistEntry[]): void {
  const deduped = entries.filter((item, index, items) => items.findIndex((entry) => entry.id === item.id) === index);
  setItem(WATCHLIST_STORAGE_KEY, deduped.slice(0, WATCHLIST_MAX_ITEMS));
}

function getWatchlistSuggestions(query: string, currentIds: Set<string>): WatchlistCatalogEntry[] {
  const normalized = query.trim().toLowerCase();
  const pool = WATCHLIST_CATALOG.filter((item) => !currentIds.has(item.id));

  if (!normalized) {
    return pool.slice(0, 8);
  }

  return pool
    .filter((item) => {
      const haystack = [item.label.toLowerCase(), item.symbol.toLowerCase(), item.meta?.toLowerCase() || '', ...item.keywords].join(' ');
      return haystack.includes(normalized);
    })
    .slice(0, 8);
}

function renderWatchlistSuggestions(): void {
  const input = document.getElementById('terminal-watchlist-search') as HTMLInputElement | null;
  const suggestions = document.getElementById('terminal-watchlist-suggestions');
  if (!input || !suggestions) return;

  const currentIds = new Set(getWatchlistEntries().map((item) => item.id));
  const results = getWatchlistSuggestions(input.value, currentIds);

  if (results.length === 0) {
    suggestions.innerHTML = '<div class="terminal-watchlist__empty">No matches found.</div>';
    return;
  }

  suggestions.innerHTML = results.map((item) => `
    <button type="button" class="terminal-watchlist__suggestion" data-watchlist-add="${item.id}">
      <span class="terminal-watchlist__suggestion-main">
        <strong>${item.label}</strong>
        <small>${item.symbol}</small>
      </span>
      <span class="terminal-watchlist__suggestion-meta">${item.meta || item.type}</span>
    </button>
  `).join('');
}

function addWatchlistEntry(entryId: string): void {
  const candidate = WATCHLIST_CATALOG.find((item) => item.id === entryId);
  if (!candidate) return;

  const current = getWatchlistEntries();
  if (current.some((item) => item.id === entryId)) return;

  saveWatchlistEntries([...current, {
    id: candidate.id,
    type: candidate.type,
    label: candidate.label,
    symbol: candidate.symbol,
    meta: candidate.meta,
  }]);
}

function removeWatchlistEntry(entryId: string): void {
  const next = getWatchlistEntries().filter((item) => item.id !== entryId);
  saveWatchlistEntries(next);
}

function bindWatchlistControls(): void {
  const container = document.getElementById('terminal-left-watchlist');
  const input = document.getElementById('terminal-watchlist-search') as HTMLInputElement | null;
  const addButton = document.getElementById('terminal-watchlist-add');
  if (!container || !input || !addButton) return;

  input.addEventListener('focus', () => {
    renderWatchlistSuggestions();
    container.classList.add('terminal-watchlist--searching');
  });

  input.addEventListener('input', () => {
    renderWatchlistSuggestions();
    container.classList.add('terminal-watchlist--searching');
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      input.value = '';
      container.classList.remove('terminal-watchlist--searching');
      renderWatchlistSuggestions();
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const first = getWatchlistSuggestions(input.value, new Set(getWatchlistEntries().map((item) => item.id)))[0];
      if (!first) return;
      addWatchlistEntry(first.id);
      input.value = '';
      renderWatchlistSuggestions();
      void refreshTickerAndWatchlist();
    }
  });

  addButton.addEventListener('click', () => {
    const first = getWatchlistSuggestions(input.value, new Set(getWatchlistEntries().map((item) => item.id)))[0];
    if (!first) return;
    addWatchlistEntry(first.id);
    input.value = '';
    renderWatchlistSuggestions();
    void refreshTickerAndWatchlist();
  });

  container.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const addTarget = target.closest<HTMLElement>('[data-watchlist-add]');
    if (addTarget?.dataset.watchlistAdd) {
      addWatchlistEntry(addTarget.dataset.watchlistAdd);
      input.value = '';
      renderWatchlistSuggestions();
      void refreshTickerAndWatchlist();
      return;
    }

    const removeTarget = target.closest<HTMLElement>('[data-watchlist-remove]');
    if (removeTarget?.dataset.watchlistRemove) {
      removeWatchlistEntry(removeTarget.dataset.watchlistRemove);
      renderWatchlistSuggestions();
      void refreshTickerAndWatchlist();
    }
  });

  if (!watchlistDismissBound) {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const activeContainer = document.getElementById('terminal-left-watchlist');
      if (!target || !activeContainer) return;
      if (!activeContainer.contains(target)) {
        activeContainer.classList.remove('terminal-watchlist--searching');
      }
    });
    watchlistDismissBound = true;
  }

  renderWatchlistSuggestions();
}

function applyWatchlistTabState(): void {
  document.querySelectorAll('.terminal-rail--left .terminal-panel__tab').forEach((node) => {
    const button = node as HTMLButtonElement;
    const isActive = button.dataset.tab === activeWatchlistTab;
    button.classList.toggle('terminal-panel__tab--active', isActive);
  });

  const container = document.getElementById('terminal-left-watchlist');
  if (container) {
    container.classList.toggle('terminal-watchlist--breadth-mode', activeWatchlistTab === 'BREADTH');
  }
}

function bindWatchlistTabs(): void {
  document.querySelectorAll('.terminal-rail--left .terminal-panel__tab').forEach((node) => {
    node.addEventListener('click', () => {
      const next = (node as HTMLButtonElement).dataset.tab;
      if (next !== 'INDICES' && next !== 'BREADTH') return;
      activeWatchlistTab = next;
      applyWatchlistTabState();
      void refreshTickerAndWatchlist();
    });
  });

  applyWatchlistTabState();
}

function resolveIndexEntry(entry: WatchlistEntry, indices: Array<{ name: string; symbol: string; pChange: number }>): { value: string; toneClass: string; meta: string } | null {
  const entryKey = normalizeKey(entry.symbol);
  const match = indices.find((item) => {
    const nameKey = normalizeKey(item.name || '');
    const symbolKey = normalizeKey(item.symbol || '');
    return nameKey === entryKey || symbolKey === entryKey || nameKey.includes(entryKey) || symbolKey.includes(entryKey);
  });

  if (!match) return null;
  const sign = match.pChange >= 0 ? '+' : '';
  return {
    value: `${sign}${match.pChange.toFixed(2)}%`,
    toneClass: match.pChange >= 0 ? 'terminal-text--up' : 'terminal-text--down',
    meta: `${match.symbol || match.name} · live`,
  };
}

function pushLatencySample(latencyMs: number): void {
  telemetryLatencySeries.push(Math.round(latencyMs));
  if (telemetryLatencySeries.length > LATENCY_SERIES_LIMIT) {
    telemetryLatencySeries = telemetryLatencySeries.slice(-LATENCY_SERIES_LIMIT);
  }
}

function formatLatencyTimeline(): string {
  if (telemetryLatencySeries.length === 0) return '--';
  const tail = telemetryLatencySeries.slice(-4).join(' > ');
  const latest = telemetryLatencySeries[telemetryLatencySeries.length - 1];
  return `${latest}ms | ${tail}`;
}

function pruneErrorEvents(now: number): void {
  const cutoff = now - STREAM_ERROR_SPIKE_WINDOW_MS;
  telemetryErrorEvents = telemetryErrorEvents.filter(ts => ts >= cutoff);
}

function recordErrorEvents(count: number = 1): void {
  const now = Date.now();
  for (let i = 0; i < count; i += 1) {
    telemetryErrorEvents.push(now);
  }
  pruneErrorEvents(now);
}

function getRecentErrorCount(now: number): number {
  pruneErrorEvents(now);
  return telemetryErrorEvents.length;
}

function getStreamHealthState(now: number): StreamHealthState {
  const recentErrors = getRecentErrorCount(now);

  if (telemetryLastSuccessAt === null) {
    return telemetryConsecutiveFailures > 0 ? 'RECONNECTING' : 'CONNECTING';
  }

  if (now - telemetryLastSuccessAt > STREAM_STALE_MS) {
    return 'STALE';
  }

  if (telemetryConsecutiveFailures > 0) {
    return 'RECONNECTING';
  }

  if (recentErrors >= STREAM_ERROR_SPIKE_THRESHOLD) {
    return 'DEGRADED';
  }

  return 'CONNECTED';
}

function applyStreamState(node: HTMLElement, state: StreamHealthState): void {
  node.textContent = state;
  node.classList.remove(
    'terminal-stream--connecting',
    'terminal-stream--connected',
    'terminal-stream--degraded',
    'terminal-stream--reconnecting',
    'terminal-stream--stale',
  );
  node.classList.add(`terminal-stream--${state.toLowerCase()}`);
}

function formatElapsed(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${Math.floor(ms / 1000)}s`;
  return `${Math.floor(ms / 60000)}m`;
}

function applyApiHealthState(node: HTMLElement, state: 'ok' | 'degraded' | 'offline', latencyMs?: number): void {
  node.classList.remove('terminal-pill--api-ok', 'terminal-pill--api-degraded', 'terminal-pill--api-offline');

  if (state === 'ok') {
    node.classList.add('terminal-pill--api-ok');
    node.textContent = `API OK${typeof latencyMs === 'number' ? ` ${Math.round(latencyMs)}ms` : ''}`;
    return;
  }

  if (state === 'degraded') {
    node.classList.add('terminal-pill--api-degraded');
    node.textContent = 'API DEGRADED';
    return;
  }

  node.classList.add('terminal-pill--api-offline');
  node.textContent = 'API OFFLINE';
}

function formatApiHealthTooltip(report: ApiHealthResponse): string {
  const lines: string[] = [];
  const checks = report.checks || {};
  const details = report.details || {};

  for (const [key, value] of Object.entries(checks)) {
    const detail = details[key] || value;
    lines.push(`${key.toUpperCase()}: ${value.toUpperCase()} (${detail})`);
  }

  if (typeof report.latencyMs === 'number') {
    lines.push(`LATENCY: ${Math.round(report.latencyMs)}ms`);
  }

  if (report.timestamp) {
    lines.push(`UPDATED: ${formatIST(new Date(report.timestamp), 'time')} IST`);
  }

  return lines.length > 0 ? lines.join('\n') : 'API health telemetry unavailable';
}

export function renderTerminalShell(pageContentHtml: string): string {
    return `
      <div class="terminal-shell">
        ${renderTerminalTopBar()}
        ${renderTerminalNav()}
        ${renderTerminalTicker()}
        <div class="terminal-layout" id="terminal-layout">
          <aside class="terminal-rail terminal-rail--left">
            ${renderPanelHeader('Watchlist', 'LIVE')}
            <div id="terminal-left-watchlist" class="terminal-panel__body terminal-watchlist">
              <div class="terminal-watchlist__controls">
                <div class="terminal-watchlist__searchbar">
                  <input id="terminal-watchlist-search" class="terminal-watchlist__input" type="text" placeholder="Add index or stock" aria-label="Add watchlist item" />
                  <button id="terminal-watchlist-add" type="button" class="terminal-watchlist__add">ADD</button>
                </div>
                <div id="terminal-watchlist-suggestions" class="terminal-watchlist__suggestions"></div>
              </div>
              <div id="terminal-left-watchlist-items" class="terminal-watchlist__items">Loading live watchlist...</div>
            </div>
            ${renderPanelTabs(['INDICES', 'BREADTH'], 'INDICES')}
          </aside>

          <main class="terminal-center">
            <div class="terminal-mobile-jump" id="terminal-mobile-jump">
              <button type="button" data-path="/">Dashboard</button>
              <button type="button" data-path="/session-radar">Radar</button>
              <button type="button" data-path="/markets">Markets</button>
              <button type="button" data-path="/strategy">Strategy</button>
              <button type="button" data-path="/intelligence">Intelligence</button>
              <button type="button" data-path="/portfolio">Portfolio</button>
            </div>
            ${pageContentHtml}
          </main>

          <aside class="terminal-rail terminal-rail--right" id="terminal-right-rail">
            ${renderPanelHeader('Intelligence Feed', 'REALTIME')}
            <div id="terminal-right-news" class="terminal-panel__body">Loading priority headlines...</div>
            ${renderStatStrip([
                { label: 'Tier A', value: '250ms-1s', tone: 'up' },
                { label: 'Tier B', value: '2s-5s', tone: 'neutral' },
                { label: 'Tier C', value: '10s-30s', tone: 'neutral' },
            ])}
          </aside>
        </div>
        <div id="terminal-global-telemetry-root"></div>
        ${renderFooterTelemetryStrip()}
      </div>
    `;
}

function parseCommand(rawInput: string): CommandAction | null {
    const raw = rawInput.trim();
    if (!raw) return null;

    const normalized = raw.toUpperCase().replace(/\s+/g, ' ');
    if (normalized === 'NEWS') return { route: '/intelligence', event: 'imm:command:news', statusLabel: 'NEWS' };
    if (normalized === 'BREADTH') return { route: '/markets', event: 'imm:command:breadth', statusLabel: 'BREADTH' };
    if (normalized === 'CHAIN') return { route: '/markets', event: 'imm:command:chain', statusLabel: 'CHAIN' };
    if (normalized === 'CHAT') return { route: '/chat', event: 'imm:command:chat', statusLabel: 'CHAT' };

    if (normalized.endsWith(' GO')) {
        const symbol = normalized.replace('SYMBOL', '').replace(' GO', '').trim();
        if (!symbol) return null;
        return {
            route: '/markets',
            event: 'imm:command:symbol',
            payload: { symbol },
          statusLabel: `SYMBOL ${symbol}`,
        };
    }

    return null;
}

function setCommandStatus(label: string): void {
  const status = document.getElementById('terminal-command-status');
  if (!status) return;
  status.textContent = label;

  if (commandStatusTimer) {
    clearTimeout(commandStatusTimer);
    commandStatusTimer = null;
  }

  if (label !== 'READY') {
    commandStatusTimer = setTimeout(() => {
      const next = document.getElementById('terminal-command-status');
      if (next) next.textContent = 'READY';
      commandStatusTimer = null;
    }, 2000);
  }
}

function bindCommands(): void {
    const form = document.getElementById('terminal-command-form') as HTMLFormElement | null;
    const input = document.getElementById('terminal-command-input') as HTMLInputElement | null;

    if (!form || !input) return;

    form.addEventListener('submit', (event) => {
        event.preventDefault();
        const action = parseCommand(input.value);
      if (!action) {
        setCommandStatus('INVALID CMD');
        return;
      }

        navigate(action.route);
        document.dispatchEvent(new CustomEvent(action.event, { detail: action.payload || {} }));
      setCommandStatus(action.statusLabel || 'EXECUTED');
        input.value = '';
    });
}

function bindNav(): void {
    document.querySelectorAll('.terminal-nav__link').forEach((node) => {
        node.addEventListener('click', () => {
            const path = node.getAttribute('data-path');
            if (!path) return;
            navigate(path);
        });
    });

    document.querySelectorAll('#terminal-mobile-jump button').forEach((node) => {
        node.addEventListener('click', () => {
            const path = node.getAttribute('data-path');
            if (!path) return;
            navigate(path);
        });
    });
}

function bindKeyboardShortcuts(): void {
  if (shortcutsBound) return;
  shortcutsBound = true;

  document.addEventListener('keydown', (event) => {
    const commandInput = document.getElementById('terminal-command-input') as HTMLInputElement | null;
    const rightRail = document.getElementById('terminal-right-rail');

    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      commandInput?.focus();
      setCommandStatus('CMD FOCUS');
      return;
    }

    if (event.key === 'Escape') {
      commandInput?.blur();
      rightRail?.classList.remove('terminal-rail--open');
      setCommandStatus('READY');
      return;
    }

    if (event.altKey) {
      const keyMap: Record<string, string> = {
        '1': '/',
        '2': '/session-radar',
        '3': '/strategy',
        '4': '/intelligence',
        '5': '/markets',
        '6': '/portfolio',
        '7': '/backtest',
      };

      const route = keyMap[event.key];
      if (route) {
        event.preventDefault();
        navigate(route);
        setCommandStatus(`GO ${route.toUpperCase()}`);
        return;
      }

      if (event.key.toLowerCase() === 'n') {
        event.preventDefault();
        navigate('/intelligence');
        document.dispatchEvent(new CustomEvent('imm:command:news'));
        setCommandStatus('NEWS');
        return;
      }

      if (event.key.toLowerCase() === 'b') {
        event.preventDefault();
        navigate('/markets');
        document.dispatchEvent(new CustomEvent('imm:command:breadth'));
        setCommandStatus('BREADTH');
        return;
      }

      if (event.key.toLowerCase() === 'c') {
        event.preventDefault();
        navigate('/chat');
        document.dispatchEvent(new CustomEvent('imm:command:chat'));
        setCommandStatus('CHAT');
        return;
      }
    }
  });
}

function bindThemeToggle(): void {
    const toggle = document.getElementById('terminal-theme-toggle');
    if (!toggle) return;

    toggle.addEventListener('click', () => {
        const current = getThemePreference();
        setThemePreference(current === 'dark' ? 'light' : 'dark');
    });
}

function bindRightRailToggle(): void {
    const toggle = document.getElementById('terminal-rightrail-toggle');
    const rightRail = document.getElementById('terminal-right-rail');
    if (!toggle || !rightRail) return;

    toggle.addEventListener('click', () => {
        rightRail.classList.toggle('terminal-rail--open');
    });
}

function bindOptionsClusterAlert(): void {
  if (optionsAlertBound) return;
  optionsAlertBound = true;

  document.addEventListener('imm:options:cluster-alert', (event) => {
    const detail = (event as CustomEvent<OptionsClusterAlertEventDetail>).detail;
    const node = document.getElementById('terminal-options-alert');
    if (!node) return;

    const level = detail?.level || 'normal';
    const cluster = detail?.cluster || 'CLUSTER';
    const score = typeof detail?.score === 'number' ? detail.score.toFixed(2) : '--';
    const dwellMs = typeof detail?.dwellMs === 'number' ? detail.dwellMs : 0;
    const dwell = formatElapsed(dwellMs);

    optionsAlertLastEventAt = Date.now();

    node.classList.remove('terminal-alert--normal', 'terminal-alert--elevated', 'terminal-alert--critical', 'terminal-alert--stale');
    node.classList.add(`terminal-alert--${level}`);
    node.textContent = `${cluster} ${level.toUpperCase()} ${score} ${dwell}`;

    if (optionsAlertStaleTimer) {
      clearTimeout(optionsAlertStaleTimer);
      optionsAlertStaleTimer = null;
    }

    if (optionsAlertStaleAgeInterval) {
      clearInterval(optionsAlertStaleAgeInterval);
      optionsAlertStaleAgeInterval = null;
    }

    optionsAlertStaleTimer = setTimeout(() => {
      const staleNode = document.getElementById('terminal-options-alert');
      if (!staleNode) return;
      staleNode.classList.remove('terminal-alert--normal', 'terminal-alert--elevated', 'terminal-alert--critical');
      staleNode.classList.add('terminal-alert--stale');
      const ageMs = optionsAlertLastEventAt ? Math.max(0, Date.now() - optionsAlertLastEventAt) : OPTIONS_ALERT_STALE_MS;
      staleNode.textContent = `STALE ${formatElapsed(ageMs)}`;

      if (optionsAlertStaleAgeInterval) {
        clearInterval(optionsAlertStaleAgeInterval);
      }
      optionsAlertStaleAgeInterval = setInterval(() => {
        const refreshNode = document.getElementById('terminal-options-alert');
        if (!refreshNode) return;
        const age = optionsAlertLastEventAt ? Math.max(0, Date.now() - optionsAlertLastEventAt) : 0;
        refreshNode.textContent = `STALE ${formatElapsed(age)}`;
      }, 1000);

      optionsAlertStaleTimer = null;
    }, OPTIONS_ALERT_STALE_MS);
  });
}

async function refreshTickerAndWatchlist(): Promise<void> {
    const ticker = document.getElementById('terminal-ticker-track');
    const watchlist = document.getElementById('terminal-left-watchlist-items');
    const statePill = document.getElementById('terminal-market-state');
    const clock = document.getElementById('terminal-clock');

    const status = getMarketStatus();
    if (statePill) statePill.textContent = status.label.toUpperCase();
    if (clock) clock.textContent = `${formatIST(getISTNow(), 'time')} IST`;

    try {
        const [indices, breadth] = await Promise.all([
            fetchIndices(),
            fetchBreadth(),
        ]);

        if (ticker) {
            if (indices.length === 0) {
                ticker.textContent = 'Live ticker unavailable';
            } else {
                ticker.innerHTML = indices.slice(0, 10).map((item) => {
                    const tone = item.change >= 0 ? 'up' : 'down';
                    const sign = item.change >= 0 ? '+' : '';
                    return `<span class="terminal-ticker__item terminal-ticker__item--${tone}">${item.symbol} ${item.last.toFixed(2)} ${sign}${item.pChange.toFixed(2)}%</span>`;
                }).join('');
            }
        }

        if (watchlist) {
            if (activeWatchlistTab === 'BREADTH') {
                watchlist.innerHTML = breadth ? `
                  <div class="terminal-watchlist__row">
                    <div class="terminal-watchlist__main">
                      <span class="terminal-watchlist__label">A/D Ratio</span>
                      <span class="terminal-watchlist__meta">${breadth.advances} advances / ${breadth.declines} declines</span>
                    </div>
                    <div class="terminal-watchlist__side">
                      <strong class="terminal-watchlist__value ${breadth.adRatio >= 1 ? 'terminal-text--up' : 'terminal-text--down'}">${breadth.adRatio.toFixed(2)}</strong>
                    </div>
                  </div>
                  <div class="terminal-watchlist__row">
                    <div class="terminal-watchlist__main">
                      <span class="terminal-watchlist__label">52W High / Low</span>
                      <span class="terminal-watchlist__meta">Breakout vs breakdown participation</span>
                    </div>
                    <div class="terminal-watchlist__side">
                      <strong class="terminal-watchlist__value">${breadth.new52wHigh}/${breadth.new52wLow}</strong>
                    </div>
                  </div>
                  <div class="terminal-watchlist__row">
                    <div class="terminal-watchlist__main">
                      <span class="terminal-watchlist__label">Unchanged</span>
                      <span class="terminal-watchlist__meta">Flat names in the basket</span>
                    </div>
                    <div class="terminal-watchlist__side">
                      <strong class="terminal-watchlist__value">${breadth.unchanged}</strong>
                    </div>
                  </div>
                  <div class="terminal-watchlist__row">
                    <div class="terminal-watchlist__main">
                      <span class="terminal-watchlist__label">Delivery %</span>
                      <span class="terminal-watchlist__meta">Participation quality</span>
                    </div>
                    <div class="terminal-watchlist__side">
                      <strong class="terminal-watchlist__value">${breadth.deliveryPercent.toFixed(1)}%</strong>
                    </div>
                  </div>
                  <div class="terminal-watchlist__row">
                    <div class="terminal-watchlist__main">
                      <span class="terminal-watchlist__label">Turnover</span>
                      <span class="terminal-watchlist__meta">Cash market activity</span>
                    </div>
                    <div class="terminal-watchlist__side">
                      <strong class="terminal-watchlist__value">₹${breadth.totalTurnover.toLocaleString('en-IN')} Cr</strong>
                    </div>
                  </div>
                ` : 'Breadth unavailable';
                return;
            }

            const entries = getWatchlistEntries();
            watchlist.innerHTML = entries.map((entry) => {
                if (entry.type === 'breadth') {
                    const breadthValue = breadth ? breadth.adRatio.toFixed(2) : 'N/A';
                    const breadthTone = breadth && breadth.adRatio >= 1 ? 'terminal-text--up' : 'terminal-text--down';
                    const breadthMeta = breadth
                      ? `${breadth.advances}A / ${breadth.declines}D`
                      : 'Market internals unavailable';

                    return `
                      <div class="terminal-watchlist__row" data-watchlist-id="${entry.id}">
                        <div class="terminal-watchlist__main">
                          <span class="terminal-watchlist__label">${entry.label}</span>
                          <span class="terminal-watchlist__meta">${breadthMeta}</span>
                        </div>
                        <div class="terminal-watchlist__side">
                          <strong class="terminal-watchlist__value ${breadthTone}">${breadthValue}</strong>
                          <button type="button" class="terminal-watchlist__remove" data-watchlist-remove="${entry.id}" aria-label="Remove ${entry.label}">×</button>
                        </div>
                      </div>
                    `;
                }

                if (entry.type === 'index') {
                    const resolved = resolveIndexEntry(entry, indices);
                    return `
                      <div class="terminal-watchlist__row" data-watchlist-id="${entry.id}">
                        <div class="terminal-watchlist__main">
                          <span class="terminal-watchlist__label">${entry.label}</span>
                          <span class="terminal-watchlist__meta">${resolved?.meta || `${entry.symbol} · awaiting live feed`}</span>
                        </div>
                        <div class="terminal-watchlist__side">
                          <strong class="terminal-watchlist__value ${resolved?.toneClass || ''}">${resolved?.value || '--'}</strong>
                          <button type="button" class="terminal-watchlist__remove" data-watchlist-remove="${entry.id}" aria-label="Remove ${entry.label}">×</button>
                        </div>
                      </div>
                    `;
                }

                return `
                  <div class="terminal-watchlist__row" data-watchlist-id="${entry.id}">
                    <div class="terminal-watchlist__main">
                      <span class="terminal-watchlist__label">${entry.label}</span>
                      <span class="terminal-watchlist__meta">${entry.symbol} · ${entry.meta || 'stock'}</span>
                    </div>
                    <div class="terminal-watchlist__side">
                      <strong class="terminal-watchlist__value terminal-watchlist__value--neutral">TRACK</strong>
                      <button type="button" class="terminal-watchlist__remove" data-watchlist-remove="${entry.id}" aria-label="Remove ${entry.label}">×</button>
                    </div>
                  </div>
                `;
            }).join('');
        }
    } catch {
        if (ticker) ticker.textContent = 'Live ticker unavailable';
        if (watchlist) watchlist.textContent = 'Watchlist unavailable';
    }
}

async function refreshRightRailNews(): Promise<void> {
    const newsContainer = document.getElementById('terminal-right-news');
    if (!newsContainer) return;

    try {
        const headlines = await fetchPriorityHeadlines();
        if (headlines.length === 0) {
            newsContainer.textContent = 'No live headlines available.';
            return;
        }

        newsContainer.innerHTML = headlines.slice(0, 6).map((item) => (
            `<a class="terminal-news__item" href="${item.link}" target="_blank" rel="noreferrer">
              <span class="terminal-news__source">${item.source}</span>
              <span class="terminal-news__title">${item.title}</span>
              <span class="terminal-news__time">${item.timeAgo}</span>
            </a>`
        )).join('');
    } catch {
        newsContainer.textContent = 'News feed unavailable.';
    }
}

async function refreshTelemetry(): Promise<void> {
    const streamState = document.getElementById('terminal-stream-state');
    const latency = document.getElementById('terminal-latency');
    const errorSpike = document.getElementById('terminal-error-spike');
    const cacheHit = document.getElementById('terminal-cache-hit');
    const cacheMiss = document.getElementById('terminal-cache-miss');
    const cacheErrors = document.getElementById('terminal-cache-errors');
    const chartBudget = document.getElementById('terminal-chart-budget');
    const updated = document.getElementById('terminal-telemetry-updated');

    if (!streamState || !latency || !errorSpike || !cacheHit || !cacheMiss || !cacheErrors || !chartBudget || !updated) return;

    const startedAt = Date.now();

    try {
        const telemetry = await apiFetch<CacheTelemetryResponse>('/cache-telemetry', {
            cacheTTL: 15,
            staleWhileRevalidate: false,
        });
        const now = Date.now();
        const latencyMs = Math.max(1, now - startedAt);
        pushLatencySample(latencyMs);
        telemetryConsecutiveFailures = 0;
        telemetryLastSuccessAt = now;

        latency.textContent = formatLatencyTimeline();

        const totals = telemetry.data.totals;
        if (!totals) {
            recordErrorEvents();
            applyStreamState(streamState, getStreamHealthState(now));
            errorSpike.textContent = `${getRecentErrorCount(now)}/120s`;
            updated.textContent = formatIST(getISTNow(), 'time');
            return;
        }

        if (previousCacheErrorTotal !== null && totals.errors > previousCacheErrorTotal) {
            recordErrorEvents(totals.errors - previousCacheErrorTotal);
        }
        previousCacheErrorTotal = totals.errors;

        applyStreamState(streamState, getStreamHealthState(now));
        errorSpike.textContent = `${getRecentErrorCount(now)}/120s`;
        cacheHit.textContent = `${totals.hits} (${totals.hitRatePct.toFixed(1)}%)`;
        cacheMiss.textContent = `${totals.misses}`;
        cacheErrors.textContent = `${totals.errors}`;

        const chartTelemetry = getChartRenderTelemetrySnapshot();
        if (chartTelemetry.count > 0) {
          chartBudget.textContent = `${chartTelemetry.overBudget}/${chartTelemetry.count} over | avg ${chartTelemetry.averageLatestMs.toFixed(2)}ms`;
        } else {
          chartBudget.textContent = 'No active chart metrics';
        }

        updated.textContent = telemetry.data.timestamp
            ? formatIST(new Date(telemetry.data.timestamp), 'time')
            : formatIST(getISTNow(), 'time');
    } catch {
        const now = Date.now();
        telemetryConsecutiveFailures += 1;
        recordErrorEvents();
        applyStreamState(streamState, getStreamHealthState(now));
        latency.textContent = formatLatencyTimeline();
        errorSpike.textContent = `${getRecentErrorCount(now)}/120s`;
    }
}

async function refreshApiHealth(): Promise<void> {
  const apiHealthPill = document.getElementById('terminal-api-health');
  if (!apiHealthPill) return;

  try {
    const response = await fetch('/api/health', { cache: 'no-store' });
    const data = await response.json() as ApiHealthResponse;

    apiHealthPill.title = formatApiHealthTooltip(data);

    if (data.status === 'ok') {
      applyApiHealthState(apiHealthPill, 'ok', data.latencyMs);
      return;
    }

    applyApiHealthState(apiHealthPill, 'degraded');
  } catch {
    apiHealthPill.title = 'Unable to reach /api/health';
    applyApiHealthState(apiHealthPill, 'offline');
  }
}

let tickerInterval: ReturnType<typeof setInterval> | null = null;
let newsInterval: ReturnType<typeof setInterval> | null = null;
let telemetryInterval: ReturnType<typeof setInterval> | null = null;
let apiHealthInterval: ReturnType<typeof setInterval> | null = null;
let clockInterval: ReturnType<typeof setInterval> | null = null;

function startLiveLoops(): void {
    void refreshTickerAndWatchlist();
    void refreshRightRailNews();
    void refreshTelemetry();
    void refreshApiHealth();

    tickerInterval = setInterval(() => void refreshTickerAndWatchlist(), 5000);
    newsInterval = setInterval(() => void refreshRightRailNews(), 30000);
    telemetryInterval = setInterval(() => void refreshTelemetry(), 10000);
    apiHealthInterval = setInterval(() => void refreshApiHealth(), 20000);
    clockInterval = setInterval(() => {
        const clock = document.getElementById('terminal-clock');
        if (clock) clock.textContent = `${formatIST(getISTNow(), 'time')} IST`;
    }, 1000);
}

export function initTerminalShell(): void {
    bindCommands();
    bindNav();
    bindThemeToggle();
    bindRightRailToggle();
  bindKeyboardShortcuts();
    bindOptionsClusterAlert();
    bindWatchlistControls();
    bindWatchlistTabs();

    const globalTelemetryRoot = document.getElementById('terminal-global-telemetry-root');
    if (globalTelemetryRoot) {
      renderPhase2GlobalTelemetryEngine(globalTelemetryRoot);
    }

    if (!tickerInterval && !newsInterval && !telemetryInterval && !apiHealthInterval && !clockInterval) {
        startLiveLoops();
    }
}

export const TERMINAL_SHELL_STYLES = `
  .terminal-shell {
    min-height: 100vh;
    background:
      radial-gradient(1200px 500px at 10% -10%, rgba(255, 122, 26, 0.10), transparent 55%),
      radial-gradient(900px 450px at 90% -20%, rgba(30, 203, 225, 0.08), transparent 56%),
      var(--terminal-bg-base);
    color: var(--terminal-text);
    font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace;
  }

  .terminal-topbar,
  .terminal-nav,
  .terminal-ticker,
  .terminal-footer {
    border-bottom: 1px solid var(--terminal-line);
    background: color-mix(in srgb, var(--terminal-bg-panel) 92%, transparent);
    backdrop-filter: blur(4px);
  }

  .terminal-topbar {
    display: grid;
    grid-template-columns: 220px 1fr auto;
    gap: 12px;
    align-items: center;
    padding: 10px 14px;
  }

  .terminal-command {
    min-width: 0;
  }

  .terminal-topbar__brand {
    color: var(--terminal-accent);
    letter-spacing: 0.08em;
    font-size: 12px;
    font-weight: 700;
  }

  .terminal-command__input {
    width: 100%;
    background: var(--terminal-bg-elevated);
    border: 1px solid color-mix(in srgb, var(--terminal-accent) 55%, transparent);
    color: var(--terminal-text);
    height: 34px;
    padding: 0 10px;
    border-radius: 4px;
    font-size: 12px;
  }

  .terminal-command__input:focus {
    outline: none;
    border-color: var(--terminal-accent);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--terminal-accent) 20%, transparent);
  }

  .terminal-topbar__status {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .terminal-pill {
    border: 1px solid var(--terminal-line);
    padding: 4px 8px;
    border-radius: 3px;
    font-size: 10px;
    background: var(--terminal-bg-elevated);
    color: var(--terminal-text-muted);
  }

  .terminal-pill--mono {
    color: var(--terminal-text);
  }

  .terminal-pill--api {
    min-width: 94px;
    text-align: center;
  }

  .terminal-pill--api-ok {
    border-color: color-mix(in srgb, var(--terminal-up) 55%, transparent);
    color: var(--terminal-up);
  }

  .terminal-pill--api-degraded {
    border-color: color-mix(in srgb, #ff9f43 60%, transparent);
    color: #ffb366;
  }

  .terminal-pill--api-offline {
    border-color: color-mix(in srgb, var(--terminal-down) 60%, transparent);
    color: var(--terminal-down);
  }

  .terminal-topbar__btn {
    border: 1px solid var(--terminal-line);
    background: var(--terminal-bg-elevated);
    color: var(--terminal-text);
    font-size: 10px;
    padding: 4px 8px;
    border-radius: 3px;
    cursor: pointer;
  }

  .terminal-nav {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 6px 10px;
    overflow-x: auto;
  }

  .terminal-nav__link {
    border: none;
    background: transparent;
    color: var(--terminal-text-muted);
    font-size: 11px;
    letter-spacing: 0.03em;
    padding: 6px 9px;
    cursor: pointer;
    border-radius: 3px;
  }

  .terminal-nav__link:hover,
  .terminal-nav__link--active {
    color: color-mix(in srgb, var(--terminal-accent) 75%, white);
    background: color-mix(in srgb, var(--terminal-accent) 18%, transparent);
  }

  .terminal-ticker {
    overflow: hidden;
    white-space: nowrap;
    padding: 8px 0;
  }

  .terminal-ticker__track {
    display: inline-flex;
    gap: 22px;
    padding-left: 12px;
    animation: terminalTicker 40s linear infinite;
  }

  .terminal-ticker__item {
    font-size: 11px;
    color: var(--terminal-text);
  }

  .terminal-ticker__item--up {
    color: var(--terminal-up);
  }

  .terminal-ticker__item--down {
    color: var(--terminal-down);
  }

  .terminal-global-telemetry {
    margin: 8px 10px 0;
    border: 1px solid var(--terminal-line);
    border-radius: 6px;
    padding: 8px;
    background: color-mix(in srgb, var(--terminal-bg-panel) 88%, transparent);
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
  }

  .terminal-global-telemetry__slot {
    border: 1px solid var(--terminal-line-soft);
    border-radius: 4px;
    padding: 6px;
    background: rgba(255, 255, 255, 0.02);
  }

  .terminal-global-telemetry__title {
    font-size: 10px;
    color: var(--terminal-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 4px;
  }

  .terminal-global-telemetry__canvas {
    width: 100%;
    height: 76px;
    display: block;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.18);
  }

  .terminal-global-telemetry__board {
    grid-column: span 3;
    border: 1px solid var(--terminal-line-soft);
    border-radius: 4px;
    padding: 6px;
    display: grid;
    gap: 4px;
    background: rgba(255, 255, 255, 0.02);
  }

  .terminal-global-telemetry__meta {
    grid-column: span 1;
    border: 1px solid var(--terminal-line-soft);
    border-radius: 4px;
    padding: 6px;
    font-size: 10px;
    color: var(--terminal-text-muted);
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.02);
  }

  .terminal-global-telemetry__stale {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: var(--terminal-text);
    background: rgba(7, 10, 14, 0.74);
  }

  @keyframes terminalTicker {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }

  .terminal-layout {
    display: grid;
    grid-template-columns: 260px minmax(0, 1fr) 320px;
    gap: 10px;
    min-height: calc(100vh - 174px);
    padding: 10px;
  }

  .terminal-rail {
    border: 1px solid var(--terminal-line);
    background: color-mix(in srgb, var(--terminal-bg-panel) 90%, transparent);
    border-radius: 6px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  .terminal-center {
    min-width: 0;
    border: 1px solid var(--terminal-line);
    border-radius: 6px;
    padding: 8px;
    background: color-mix(in srgb, var(--terminal-bg-panel) 86%, transparent);
    overflow: auto;
  }

  .terminal-panel__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border-bottom: 1px solid var(--terminal-line-soft);
  }

  .terminal-panel__title {
    font-size: 11px;
    letter-spacing: 0.05em;
    color: var(--terminal-accent);
    margin: 0;
  }

  .terminal-panel__meta {
    color: var(--terminal-text-muted);
    font-size: 10px;
  }

  .terminal-panel__body {
    padding: 10px;
    font-size: 12px;
    color: var(--terminal-text);
    display: grid;
    gap: 7px;
  }

  .terminal-watchlist {
    position: relative;
    align-content: start;
  }

  .terminal-watchlist__controls {
    display: grid;
    gap: 8px;
    margin-bottom: 4px;
  }

  .terminal-watchlist--breadth-mode .terminal-watchlist__controls {
    display: none;
  }

  .terminal-watchlist__searchbar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 6px;
  }

  .terminal-watchlist__input {
    min-width: 0;
    border: 1px solid var(--terminal-line);
    background: var(--terminal-bg-elevated);
    color: var(--terminal-text);
    border-radius: 4px;
    height: 30px;
    padding: 0 9px;
    font-size: 11px;
  }

  .terminal-watchlist__input:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--terminal-accent) 70%, transparent);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--terminal-accent) 18%, transparent);
  }

  .terminal-watchlist__add {
    border: 1px solid color-mix(in srgb, var(--terminal-accent) 48%, transparent);
    background: color-mix(in srgb, var(--terminal-accent) 14%, var(--terminal-bg-elevated));
    color: var(--terminal-text);
    border-radius: 4px;
    padding: 0 10px;
    font-size: 10px;
    letter-spacing: 0.06em;
    cursor: pointer;
  }

  .terminal-watchlist__suggestions {
    display: none;
    gap: 6px;
    max-height: 210px;
    overflow: auto;
    padding: 6px;
    border: 1px solid var(--terminal-line-soft);
    border-radius: 4px;
    background: color-mix(in srgb, var(--terminal-bg-elevated) 94%, transparent);
  }

  .terminal-watchlist--searching .terminal-watchlist__suggestions {
    display: grid;
  }

  .terminal-watchlist__suggestion {
    border: 1px solid var(--terminal-line-soft);
    background: rgba(255, 255, 255, 0.02);
    color: var(--terminal-text);
    border-radius: 4px;
    padding: 7px 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    text-align: left;
    cursor: pointer;
  }

  .terminal-watchlist__suggestion:hover {
    border-color: color-mix(in srgb, var(--terminal-accent) 55%, transparent);
    background: color-mix(in srgb, var(--terminal-accent) 10%, rgba(255, 255, 255, 0.02));
  }

  .terminal-watchlist__suggestion-main {
    display: grid;
    gap: 2px;
    min-width: 0;
  }

  .terminal-watchlist__suggestion-main strong {
    font-size: 11px;
    color: var(--terminal-text);
  }

  .terminal-watchlist__suggestion-main small,
  .terminal-watchlist__suggestion-meta,
  .terminal-watchlist__empty,
  .terminal-watchlist__meta {
    font-size: 10px;
    color: var(--terminal-text-muted);
  }

  .terminal-watchlist__items {
    display: grid;
    gap: 7px;
  }

  .terminal-watchlist__row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    border-bottom: 1px solid color-mix(in srgb, var(--terminal-line-soft) 75%, transparent);
    padding-bottom: 7px;
  }

  .terminal-watchlist__main {
    display: grid;
    gap: 2px;
    min-width: 0;
  }

  .terminal-watchlist__label {
    color: var(--terminal-text);
    font-size: 11px;
  }

  .terminal-watchlist__side {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .terminal-watchlist__value {
    font-size: 11px;
    color: var(--terminal-text);
  }

  .terminal-watchlist__value--neutral {
    color: var(--terminal-info);
  }

  .terminal-watchlist__remove {
    border: 1px solid var(--terminal-line-soft);
    background: transparent;
    color: var(--terminal-text-muted);
    width: 20px;
    height: 20px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 11px;
    line-height: 1;
  }

  .terminal-watchlist__remove:hover {
    color: var(--terminal-down);
    border-color: color-mix(in srgb, var(--terminal-down) 50%, transparent);
  }

  .terminal-list__row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    border-bottom: 1px solid color-mix(in srgb, var(--terminal-line-soft) 75%, transparent);
    padding-bottom: 5px;
  }

  .terminal-text--up {
    color: var(--terminal-up);
  }

  .terminal-text--down {
    color: var(--terminal-down);
  }

  .terminal-panel__tabs {
    display: flex;
    gap: 6px;
    padding: 8px 10px;
    border-top: 1px solid var(--terminal-line-soft);
  }

  .terminal-panel__tab {
    border: 1px solid var(--terminal-line);
    background: var(--terminal-bg-elevated);
    color: var(--terminal-text-muted);
    font-size: 10px;
    padding: 4px 8px;
    border-radius: 3px;
  }

  .terminal-panel__tab--active {
    color: color-mix(in srgb, var(--terminal-accent) 75%, white);
    border-color: color-mix(in srgb, var(--terminal-accent) 55%, transparent);
  }

  .terminal-news__item {
    display: block;
    padding: 8px;
    border: 1px solid var(--terminal-line-soft);
    border-radius: 4px;
    color: var(--terminal-text);
    text-decoration: none;
    background: rgba(255, 255, 255, 0.02);
  }

  .terminal-news__source {
    display: block;
    color: var(--terminal-info);
    font-size: 10px;
  }

  .terminal-news__title {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.4;
  }

  .terminal-news__time {
    display: block;
    margin-top: 4px;
    font-size: 10px;
    color: var(--terminal-text-muted);
  }

  .terminal-stat-strip {
    margin: 10px;
    display: grid;
    gap: 6px;
  }

  .terminal-stat {
    border: 1px solid var(--terminal-line-soft);
    border-radius: 4px;
    padding: 6px 8px;
    background: rgba(255, 255, 255, 0.02);
  }

  .terminal-stat__label {
    display: block;
    font-size: 10px;
    color: var(--terminal-text-muted);
  }

  .terminal-stat__value {
    font-size: 11px;
    color: var(--terminal-text);
  }

  .terminal-stat--up .terminal-stat__value {
    color: var(--terminal-up);
  }

  .terminal-mobile-jump {
    display: none;
    gap: 6px;
    margin-bottom: 8px;
    overflow-x: auto;
    white-space: nowrap;
  }

  .terminal-mobile-jump button {
    border: 1px solid var(--terminal-line);
    background: var(--terminal-bg-elevated);
    color: var(--terminal-text);
    font-size: 10px;
    padding: 4px 8px;
    border-radius: 3px;
  }

  .terminal-footer {
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 8px 12px;
    border-top: 1px solid var(--terminal-line);
    border-bottom: none;
    font-size: 10px;
    overflow-x: auto;
    white-space: nowrap;
  }

  .terminal-footer__item {
    color: var(--terminal-text-muted);
  }

  .terminal-footer__item strong {
    color: var(--terminal-text);
  }

  #terminal-stream-state {
    color: var(--terminal-info);
  }

  #terminal-stream-state.terminal-stream--connecting {
    color: var(--terminal-info);
  }

  #terminal-stream-state.terminal-stream--connected {
    color: var(--terminal-up);
  }

  #terminal-stream-state.terminal-stream--degraded {
    color: #ff9f43;
  }

  #terminal-stream-state.terminal-stream--reconnecting {
    color: #ff9f43;
  }

  #terminal-stream-state.terminal-stream--stale {
    color: #ffcf5a;
  }

  #terminal-options-alert.terminal-alert--normal {
    color: var(--terminal-text-muted);
  }

  #terminal-options-alert.terminal-alert--elevated {
    color: #ff9f43;
  }

  #terminal-options-alert.terminal-alert--critical {
    color: #d94736;
  }

  #terminal-options-alert.terminal-alert--stale {
    color: #ffcf5a;
  }

  @media (max-width: 1180px) {
    .terminal-global-telemetry {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .terminal-global-telemetry__board,
    .terminal-global-telemetry__meta {
      grid-column: span 2;
    }

    .terminal-layout {
      grid-template-columns: 240px minmax(0, 1fr);
      position: relative;
    }

    .terminal-rail--right {
      position: absolute;
      right: 10px;
      top: 10px;
      width: min(340px, 88vw);
      max-height: calc(100% - 20px);
      transform: translateX(105%);
      transition: transform 180ms ease;
      z-index: 20;
    }

    .terminal-rail--right.terminal-rail--open {
      transform: translateX(0);
    }
  }

  @media (max-width: 860px) {
    .terminal-global-telemetry {
      grid-template-columns: 1fr;
    }

    .terminal-global-telemetry__board,
    .terminal-global-telemetry__meta {
      grid-column: span 1;
    }

    .terminal-topbar {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    .terminal-layout {
      grid-template-columns: 1fr;
      min-height: auto;
    }

    .terminal-rail--left,
    .terminal-rail--right {
      position: static;
      transform: none;
      width: auto;
      max-height: none;
    }

    .terminal-mobile-jump {
      display: flex;
    }

    .terminal-footer {
      padding-bottom: 66px;
    }
  }
`;
